import { Fragment, memo } from 'react';
import { Flex, Td, Text, Tr } from '@chakra-ui/react';
import { AiOutlineArrowDown, AiOutlineArrowUp } from 'react-icons/ai';
import { useSelector } from 'react-redux';

import { NewBlockOutput, NewTotalValues } from 'models/response/blockOutput.interface';
import { getVarianceFilterTable } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors, PlanPageThemeColors } from 'redux/ThemeSlice';
import { getDataTypeBasedValuewithNewValue } from 'utils/FormatValues';

import {
  PLAN_KPI_SELECTED_BG,
  PLAN_KPI_SELECTED_BORDER,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
  PLAN_TEXT,
} from '../planPageRevampUi';

interface Properties {
  row: NewBlockOutput;
  expandKey: string;
}

const removeSign = (value: number) => (value < 0 ? -1 * value : value);

const getCellBackground = (
  themeColors: PlanPageThemeColors,
  outputFormat: string,
  columnName: string,
  expandKey: string,
  isExpandedColumn?: boolean,
) => {
  if (isExpandedColumn) return PLAN_KPI_SELECTED_BG;
  if (columnName === expandKey) return PLAN_KPI_SELECTED_BG;
  if (outputFormat === 'subtotal') return PLAN_KPI_SELECTED_BG;
  return themeColors.surface;
};

const PlanCompareDataTableRow = ({ row, expandKey }: Properties) => {
  const varianceFilter = useSelector(getVarianceFilterTable);
  const themeColors = useSelector(getPlanPageThemeColors);

  const metricCellStyles = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    bg: PLAN_KPI_SELECTED_BG,
    py: 3,
    px: 3,
    minW: PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_KPI_SELECTED_BORDER,
    ...PLAN_TEXT.tableComfortaa,
    color: themeColors.textDark,
    whiteSpace: 'nowrap' as const,
  };

  const valueCellStyles = {
    py: 3,
    px: 5,
    ...PLAN_TEXT.tableComfortaaCell,
    color: themeColors.textDark,
    textAlign: 'end' as const,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_TABLE_CELL_BORDER,
    whiteSpace: 'nowrap' as const,
  };

  const diffCellStyles = {
    py: 3,
    px: 3,
    ...PLAN_TEXT.labelSmMedium,
    textAlign: 'end' as const,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_TABLE_CELL_BORDER,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Tr bg={themeColors.surface} _hover={{ bg: themeColors.surface }}>
      <Td {...metricCellStyles}>{row?.name}</Td>
      {Object.values(row.total_values as unknown as NewTotalValues[]).map((item, index) => {
        if (!item.isShow) return <Fragment key={`hidden-${index}`} />;

        const cellBg = getCellBackground(
          themeColors,
          row.output_format,
          item.name,
          expandKey,
          Boolean(item.isExpandedColumn),
        );

        const varianceValue = varianceFilter ? Number(item?.varience) : Number(item?.diff);
        const isPositive = varianceFilter ? Number(item?.varience) > 0 : Number(item?.diff) > 0;

        return (
          <Fragment key={`${item.name}-${index}`}>
            <Td {...valueCellStyles} bg={cellBg}>
              {item.value === null
                ? row.format_properties?.null_value ?? '-'
                : getDataTypeBasedValuewithNewValue(
                    row?.type,
                    item?.value || 0,
                    row?.format_properties,
                  )}
            </Td>
            <Td {...diffCellStyles} bg={cellBg}>
              {varianceValue !== 0 && (
                <Flex alignItems='center' justifyContent='flex-end' gap={1}>
                  {isPositive ? (
                    <AiOutlineArrowUp size={10} color={themeColors.success} />
                  ) : (
                    <AiOutlineArrowDown size={10} color={themeColors.danger} />
                  )}
                  <Text
                    {...PLAN_TEXT.labelSmMedium}
                    color={isPositive ? themeColors.success : themeColors.danger}
                  >
                    {varianceFilter
                      ? `${removeSign(Number(item?.varience)).toFixed(1)}%`
                      : removeSign(Number(item?.diff)).toFixed(1)}
                  </Text>
                </Flex>
              )}
            </Td>
          </Fragment>
        );
      })}
    </Tr>
  );
};

export default memo(PlanCompareDataTableRow);
